import React, { useRef, useState } from 'react';
import { Dropdown, MenuProps, Popover, Modal, message } from 'antd';
import { Edit3, Trash2, MoreVertical, Lock, SendHorizontal, Smile } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { canModifyComment, canSeeLockedComments, mentionQueryAt, socialApi, timeAgo, type Comment, type Me, type Mention, type Post } from '../socialStore';
import { searchStaff, toMention, useStaffOptions, type StaffOption } from '../useStaff';
import { comingSoon } from '../socialApi';

const QUICK_EMOJI = ['😀', '😂', '😍', '👍', '👏', '🎉', '🙏', '💪', '❤️', '🔥', '😢', '😮'];

function EmojiPicker({ onPick }: { onPick: (e: string) => void }) {
  return (
    <Popover trigger="click" placement="topRight" content={
      <div className="grid grid-cols-6 gap-1">
        {QUICK_EMOJI.map(e => <button key={e} type="button" onClick={() => onPick(e)} className="text-xl w-8 h-8 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">{e}</button>)}
      </div>
    }>
      <button type="button" className="text-gray-400 hover:text-gray-600 transition cursor-pointer" title="Biểu tượng cảm xúc"><Smile className="w-[18px] h-[18px]" /></button>
    </Popover>
  );
}

/** Tô màu các "@Tên" đã nhắc trong bình luận */
export function MentionText({ text, mentions }: { text: string; mentions?: Mention[] }) {
  if (!mentions?.length) return <>{text}</>;
  const names = mentions.map(m => m.name).sort((a, b) => b.length - a.length).map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(@(?:${names.join('|')}))`, 'g'));
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1
        ? <span key={i} className="font-semibold text-green-700 dark:text-green-400">{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

/** Ô nhập bình luận: Enter để gửi, Shift+Enter xuống dòng, gõ @ để nhắc tên đồng nghiệp */
export function CommentInput({ me, placeholder, onSubmit, autoFocus, inputRef, small }: {
  me: Me; placeholder: string; onSubmit: (text: string, mentions: Mention[]) => boolean | void; autoFocus?: boolean; inputRef?: React.Ref<HTMLTextAreaElement>; small?: boolean;
}) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<Mention[]>([]);
  const [query, setQuery] = useState<{ start: number; q: string } | null>(null);
  const [active, setActive] = useState(0);
  const [wantStaff, setWantStaff] = useState(false);
  const { list: staff, loading } = useStaffOptions(wantStaff);
  const own = useRef<HTMLTextAreaElement | null>(null);
  const setRef = (el: HTMLTextAreaElement | null) => {
    own.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  };
  const options = query ? searchStaff(staff, query.q, 6) : [];

  const update = (value: string, caret: number) => {
    setText(value);
    const q = mentionQueryAt(value, caret);
    if (q) setWantStaff(true);
    setQuery(q);
    setActive(0);
  };

  const choose = (s: StaffOption) => {
    if (!query) return;
    const caret = own.current?.selectionStart ?? text.length;
    const insert = `@${s.name} `;
    const next = text.slice(0, query.start) + insert + text.slice(caret);
    setText(next);
    setPicked(p => [...p.filter(x => x.id !== s.value), toMention(s)]);
    setQuery(null);
    const pos = query.start + insert.length;
    requestAnimationFrame(() => { own.current?.focus(); own.current?.setSelectionRange(pos, pos); });
  };

  const send = () => {
    if (!text.trim()) return;
    if (onSubmit(text, picked) !== false) { setText(''); setPicked([]); setQuery(null); }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (query && options.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (i + 1) % options.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (i - 1 + options.length) % options.length); return; }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.nativeEvent.isComposing) { e.preventDefault(); choose(options[active]); return; }
    }
    if (query && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setQuery(null); return; }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex items-start space-x-2">
      <UserAvatar name={me.name} src={me.avatar} size={small ? 28 : 32} />
      <div className="relative flex-1 flex items-end bg-gray-100 dark:bg-gray-700 focus-within:bg-white dark:focus-within:bg-gray-800 focus-within:ring-1 focus-within:ring-green-500 rounded-2xl pl-3 pr-2 py-1.5 transition">
        {query && (
          <div className="absolute left-0 bottom-full mb-1 w-72 max-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-30 overflow-hidden" role="listbox">
            {loading ? <div className="px-3 py-2 text-xs text-gray-500">Đang tải danh sách nhân sự...</div>
              : options.length ? options.map((s, i) => (
                <button key={s.value} type="button" role="option" aria-selected={i === active}
                  onMouseDown={e => { e.preventDefault(); choose(s); }}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left cursor-pointer ${i === active ? 'bg-green-50 dark:bg-green-900/30' : ''}`}>
                  <UserAvatar name={s.name} src={s.avatar} size={24} />
                  <span className="min-w-0">
                    <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">{s.name}</span>
                    {s.desc && <span className="block text-[11px] text-gray-500 truncate">{s.desc}</span>}
                  </span>
                </button>
              )) : <div className="px-3 py-2 text-xs text-gray-500">Không tìm thấy “{query.q}”</div>}
          </div>
        )}
        <textarea
          ref={setRef}
          rows={1}
          value={text}
          autoFocus={autoFocus}
          onChange={e => update(e.target.value, e.target.selectionStart)}
          onKeyDown={onKeyDown}
          onBlur={() => setQuery(null)}
          placeholder={placeholder}
          maxLength={2000}
          className={`flex-1 bg-transparent outline-none resize-none text-gray-800 dark:text-gray-200 leading-5 max-h-32 ${small ? 'text-xs' : 'text-sm'}`}
          style={{ fieldSizing: 'content' } as React.CSSProperties}
        />
        <div className="flex items-center gap-2 pl-2 pb-0.5">
          <EmojiPicker onPick={e => setText(t => t + e)} />
          <button type="button" onClick={send} disabled={!text.trim()} title="Gửi" className="text-green-600 disabled:text-gray-300 dark:disabled:text-gray-600 cursor-pointer disabled:cursor-default">
            <SendHorizontal className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({ post, c, me, parentId, onReply, now }: { post: Post; c: Comment; me: Me; parentId?: string; onReply: (id: string, name: string) => void; now: number }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.content);
  const liked = c.likes.includes(me.id);
  const small = !!parentId;

  const save = () => {
    if (!draft.trim()) return;
    socialApi.editComment(post.id, c.id, draft);
    setEditing(false);
  };
  const items: MenuProps['items'] = [
    { key: 'edit', label: 'Chỉnh sửa', icon: <Edit3 className="w-4 h-4 text-gray-500" />, onClick: () => { setDraft(c.content); setEditing(true); } },
    {
      key: 'delete', danger: true, label: 'Xóa bình luận', icon: <Trash2 className="w-4 h-4" />,
      onClick: () => Modal.confirm({
        title: 'Xóa bình luận này?', content: c.replies.length ? `Các phản hồi (${c.replies.length}) cũng sẽ bị xóa.` : undefined,
        okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
        onOk: () => { socialApi.deleteComment(post.id, c.id); message.success('Đã xóa bình luận'); },
      }),
    },
  ];

  return (
    <div className={`flex items-start space-x-2 group/comment ${small ? 'ml-10' : ''}`}>
      <UserAvatar name={c.authorName} src={c.authorAvatar} size={small ? 28 : 32} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-1">
          <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-3 py-2 min-w-0 max-w-full">
            <span className="font-bold text-gray-900 dark:text-gray-100 text-xs">{c.authorName}</span>
            {editing ? (
              <div className="mt-1">
                <textarea
                  value={draft}
                  autoFocus
                  rows={2}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); save(); }
                    if (e.key === 'Escape') setEditing(false);
                  }}
                  className="w-72 max-w-full bg-white dark:bg-gray-800 rounded px-2 py-1 text-xs text-gray-800 dark:text-gray-200 outline-none border border-gray-300 dark:border-gray-600 resize-none"
                />
                <div className="text-[11px] text-gray-400">Enter để lưu · Esc để <button type="button" className="text-green-600 hover:underline" onClick={() => setEditing(false)}>hủy</button></div>
              </div>
            ) : (
              <p className="text-[13px] text-gray-800 dark:text-gray-300 mt-0.5 break-words whitespace-pre-line"><MentionText text={c.content} mentions={c.mentions} /></p>
            )}
          </div>
          {canModifyComment(c, me) && !editing && (
            <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
              <button className="w-6 h-6 mt-1 rounded-full opacity-0 group-hover/comment:opacity-100 focus:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 cursor-pointer flex-shrink-0">
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </Dropdown>
          )}
        </div>
        <div className="flex items-center space-x-3 text-xs font-bold text-gray-500 dark:text-gray-400 mt-1 ml-2">
          <button onClick={() => socialApi.toggleCommentLike(post.id, c.id, me.id)} className={`hover:underline cursor-pointer ${liked ? 'text-green-600' : ''}`}>
            {liked ? 'Đã thích' : 'Thích'}
          </button>
          {!post.commentsLocked && (
            <button onClick={() => onReply(parentId ?? c.id, c.authorName)} className="hover:underline cursor-pointer">Phản hồi</button>
          )}
          <span className="font-normal text-gray-400" title={new Date(c.createdAt).toLocaleString('vi-VN')}>{timeAgo(c.createdAt, now)}{c.editedAt ? ' · đã chỉnh sửa' : ''}</span>
          {c.likes.length > 0 && <span className="font-normal text-[11px] bg-white dark:bg-gray-700 rounded-full px-1.5 shadow-sm border border-gray-100 dark:border-gray-600">👍 {c.likes.length}</span>}
        </div>
      </div>
    </div>
  );
}

/** Danh sách bình luận + phản hồi; ở bảng tin chỉ hiện 2 bình luận mới nhất */
export function CommentSection({ post, me, now, collapsed = true, inputRef }: { post: Post; me: Me; now: number; collapsed?: boolean; inputRef?: React.Ref<HTMLTextAreaElement> }) {
  const [showAll, setShowAll] = useState(!collapsed);
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);

  // "Chặn và ẩn bình luận": người xem không thấy bình luận cũ, chỉ tác giả và quản trị còn thấy để xử lý
  if (post.commentsLocked && !canSeeLockedComments(post, me)) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
        <Lock className="w-3.5 h-3.5" /> Tác giả đã tắt và ẩn bình luận của bài viết này.
      </div>
    );
  }

  const list = showAll ? post.comments : post.comments.slice(-2);
  const hiddenCount = post.comments.length - list.length;

  // API bình luận: Đợt 5 — giữ ô nhập, chưa gửi (không ghi vào localStorage lẫn với bài thật)
  const submit = (_text: string, _mentions: Mention[], _parentId?: string) => {
    comingSoon('Bình luận');
    return false;
  };

  // Bài từ API có số bình luận nhưng chưa có API đọc danh sách (Đợt 5) → báo rõ thay vì để trống
  const listPending = !!post.stats?.comments && !post.comments.length;

  return (
    <div className="space-y-3">
      {listPending && (
        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          Bài có {post.stats!.comments} bình luận — phần xem bình luận đang được hoàn thiện, sẽ sớm có.
        </div>
      )}
      {hiddenCount > 0 && (
        <button onClick={() => setShowAll(true)} className="text-[13px] font-bold text-gray-500 hover:underline cursor-pointer">
          Xem thêm {hiddenCount} bình luận trước
        </button>
      )}
      {list.map(c => (
        <div key={c.id} className="space-y-2">
          <CommentItem post={post} c={c} me={me} now={now} onReply={(id, name) => setReplyTo({ id, name })} />
          {c.replies.map(r => <CommentItem key={r.id} post={post} c={r} me={me} now={now} parentId={c.id} onReply={(id, name) => setReplyTo({ id, name })} />)}
          {replyTo?.id === c.id && (
            <div className="ml-10">
              <CommentInput me={me} small autoFocus placeholder={`Phản hồi ${replyTo.name}...`} onSubmit={(t, m) => submit(t, m, c.id)} />
            </div>
          )}
        </div>
      ))}
      {post.commentsLocked ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          <Lock className="w-3.5 h-3.5" /> Bình luận đã bị tắt và ẩn với mọi người{post.comments.length ? ' — chỉ tác giả và quản trị còn thấy các bình luận trên' : ''}.
        </div>
      ) : (
        <CommentInput me={me} inputRef={inputRef} placeholder="Viết bình luận... (gõ @ để nhắc tên, Enter để gửi)" onSubmit={(t, m) => submit(t, m)} />
      )}
    </div>
  );
}
